import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { DatePicker } from "@material-ui/pickers";
import { makeStyles } from "@material-ui/styles";
import { Card, Checkbox, FormControlLabel, Grid } from "@material-ui/core";
import { SearchDropdown } from "frame/components";
import moment from "moment";
import { isNotNullOrEmpty, useTerminology } from "utils";
import { useSelector } from "react-redux";
import EquipmentMultiSelect from "./equipment-multiselect";

const useStyles = makeStyles((theme) => ({
	root: {
		margin: theme.spacing(2, 0),
		padding: theme.spacing(2, 3)
	},
	heading: {
		margin: 0
	},
	formGroup: {
		margin: theme.spacing(0),
		padding: "5px 0px",
		"& >*": {
			margin: "5px 0px"
		}
	}
}));

const SiteForm = ({ values, onChange, disableSite, disableDate, hideDate, searchDropdownReadOnly = false, isAdmin = false, hideTaskTypes = true, hideEquipment = true, allowEquipmentUsage = false, customLabel, children }) => {
	const classes = useStyles();
	const { siteLabel: terminologySiteLabel } = useTerminology();
	const siteList = useSelector(({ sites }) => sites.pagination?.results || []);
	const taskTypeList = useSelector(({ taskTypes }) => taskTypes.data || []);
	const equipmentList = useSelector(({ equipment }) => equipment?.data || []);
	const featureFlags = useSelector(({ featureFlags }) => featureFlags);
	const workTypesEnabled = featureFlags?.workTypes !== false; // Default to true if not loaded
	const equipmentEnabled = featureFlags?.equipment === true; // Default to false if not loaded
	const equipmentUsageRecordingEnabled =
		featureFlags?.equipmentUsageRecording === true;
	// Hide task types if feature is disabled OR if hideTaskTypes prop is true
	const shouldHideTaskTypes = hideTaskTypes || !workTypesEnabled;
	// Hide equipment if feature is disabled OR if hideEquipment prop is true
	const shouldHideEquipment = hideEquipment || !equipmentEnabled;
	const disabled = values.save ? values.save.saving : values.isLoading;
	const siteOptions = isNotNullOrEmpty(siteList)
		? siteList
			.filter((site) => {
				// Only show active sites (leave types are now managed separately)
				return site.status && site.status.toLowerCase() === "active";
			})
			.map((site) => ({
				label: site.siteName,
				value: site.id
			}))
			.sort((a, b) => (a.label > b.label ? 1 : -1))
		: [];
	const taskTypeOptions = isNotNullOrEmpty(taskTypeList)
		? taskTypeList
			.filter((tt) => tt.active
			)
			.map((tt) => ({
				label: tt.taskType,
				value: tt.id
			}))
			.sort((a, b) => (a.label > b.label ? 1 : -1))
		: [];
	const equipmentOptions = isNotNullOrEmpty(equipmentList)
		? equipmentList
			.filter((eq) => eq.active !== false)
			.map((eq) => ({
				label: eq.equipment,
				value: eq.id,
				maintenanceType: eq.maintenanceType,
				currentHoursKms: eq.currentHoursKms
			}))
			.sort((a, b) => (a.label > b.label ? 1 : -1))
		: [];
	const captureEquipmentUsage =
		allowEquipmentUsage &&
		equipmentUsageRecordingEnabled &&
		!!values.captureEquipmentUsage;

	const onSelectOption = useCallback(
		(key) => (value) => {
			onChange({
				...values,
				[key]: value
			});
		},
		[values, onChange]
	);

	const onSelectDate = useCallback(
		(key) => (value) => {
			onChange({
				...values,
				[key]: moment(value).format()
			});
		},
		[values, onChange]
	);

	// Use custom label if provided, otherwise fall back to the org terminology label
	const siteLabel = customLabel || `Select ${terminologySiteLabel} *`;

	// If there's only one active site available, preselect it automatically.
	// This improves UX on timesheets and other flows where Site is required.
	useEffect(() => {
		const currentSiteId = values.siteId === "NO-ID" ? "" : values.siteId;
		if (!currentSiteId && siteOptions.length === 1) {
			onChange({
				...values,
				siteId: siteOptions[0].value,
			});
		}
	}, [siteOptions, values, onChange]);

	return (
		<Card className={classes.root}>
			<div className={classes.formGroup}>
				<Grid container spacing={2}>
					<Grid item md={6} xs={12}>
						<SearchDropdown
							id="site"
							data-cy="site"
							label={siteLabel}
							disabled={disabled || disableSite}
							defaultOptions={siteOptions}
							value={values.siteId !== "NO-ID" ? values.siteId : ""}
							onChange={onSelectOption("siteId")}
							readOnly={searchDropdownReadOnly}
						/>
					</Grid>
					{shouldHideTaskTypes ? null : (
						<Grid item md={6} xs={12}>
							<SearchDropdown
								id="taskType"
								data-cy="taskType"
								label="Select Work Type *"
								disabled={disabled || disableSite || taskTypeOptions.length === 0}
								defaultOptions={taskTypeOptions}
								value={values.organisationTaskTypeId || ""}
								onChange={onSelectOption("organisationTaskTypeId")}
								readOnly={searchDropdownReadOnly}
								props={
									taskTypeOptions.length === 0
										? {
												helperText:
													"No Work Types are configured for this organisation yet. Add one via “Manage Work Types”.",
										  }
										: undefined
								}
							/>
						</Grid>
					)}
				{equipmentOptions.length === 0 || shouldHideEquipment ? null : (
					<Grid item xs={12}>
						{allowEquipmentUsage &&
							equipmentUsageRecordingEnabled &&
							(values.equipment || []).length > 0 && (
							<FormControlLabel
								control={
									<Checkbox
										checked={!!values.captureEquipmentUsage}
										disabled={disabled || disableSite}
										onChange={(e) =>
											onChange({
												...values,
												captureEquipmentUsage: e.target.checked,
												equipment: (values.equipment || []).map((item) => ({
													...item,
													captureUsage: e.target.checked,
												})),
											})
										}
										color="primary"
									/>
								}
								label="Capture equipment usage"
							/>
						)}
						<EquipmentMultiSelect
							options={equipmentOptions}
							value={values.equipment || []}
							onChange={(equipment) =>
								onChange({
									...values,
									equipment: equipment.map((item) => ({
										...item,
										captureUsage: captureEquipmentUsage,
									})),
								})
							}
							disabled={disabled || disableSite}
							captureUsage={captureEquipmentUsage}
						/>
					</Grid>
				)}
				</Grid>
				{children}
			</div>
			{!hideDate && (
				<div className={classes.formGroup}>
					<DatePicker
						data-cy="date"
						disabled={disabled || disableDate}
						format="Do MMMM YYYY"
						fullWidth
						inputVariant="outlined"
						label="Select Date"
						value={values.createdAt}
						variant="outlined"
						disableFuture={true}
						onChange={onSelectDate("createdAt")}
					/>
				</div>
			)}
		</Card>
	);
};

SiteForm.propTypes = {
	className: PropTypes.string,
	values: PropTypes.object.isRequired,
	onChange: PropTypes.func.isRequired,
	disableSite: PropTypes.bool,
	disableDate: PropTypes.bool,
	hideDate: PropTypes.bool,
	searchDropdownReadOnly: PropTypes.bool,
	isAdmin: PropTypes.bool,
	hideTaskTypes: PropTypes.bool,
	hideEquipment: PropTypes.bool,
	allowEquipmentUsage: PropTypes.bool,
	customLabel: PropTypes.string,
	children: PropTypes.node
};

export default SiteForm;
